import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * FounderQuote. Editorial layout for the onboarding welcome - portrait
 * on the left, body block on the right. The body holds an eyebrow,
 * a serif headline, a lede paragraph, the founder's italic signature,
 * and an optional CTA slot.
 *
 * Stacks vertically on small viewports. Falls back to a portrait-less
 * single column when no portrait is supplied (future testimonial
 * variants that ship without a photograph).
 *
 * The image src + alt are caller-owned so this primitive doesn't
 * couple to any specific personality - tomorrow's signature could be
 * a clinician, a member, or a team.
 */
export interface FounderQuoteProps {
  portraitSrc?: string;
  portraitAlt?: string;
  /** "A note from" / "From the founder" / etc. (mono caps). */
  portraitLabel?: ReactNode;
  /** Mono caps eyebrow above the headline. */
  eyebrow?: ReactNode;
  headline: ReactNode;
  /** Body prose. Inline <em> + <b> get the brand emphasis styles. */
  body: ReactNode;
  /** Italic Newsreader signature, e.g. "Arianna". */
  signature?: ReactNode;
  /** Caption under the signature (e.g. "Arianna Huffington · Founder"). */
  signatureCaption?: ReactNode;
  /** Optional CTA slot (button + helper text). */
  cta?: ReactNode;
  className?: string;
}

export function FounderQuote({
  portraitSrc,
  portraitAlt,
  portraitLabel,
  eyebrow,
  headline,
  body,
  signature,
  signatureCaption,
  cta,
  className,
}: FounderQuoteProps) {
  return (
    <article
      className={cn(
        "grid items-start gap-8",
        portraitSrc ? "sm:grid-cols-[180px_minmax(0,1fr)]" : "grid-cols-1",
        className,
      )}
    >
      {portraitSrc ? (
        <figure className="m-0 flex flex-col items-start gap-2">
          <img
            src={portraitSrc}
            alt={portraitAlt ?? ""}
            loading="eager"
            decoding="async"
            className="block w-full max-w-[180px] rounded-2xl object-cover shadow-card"
          />
          {portraitLabel ? (
            <figcaption className="font-sans text-xs font-medium text-ink-tertiary">
              {portraitLabel}
            </figcaption>
          ) : null}
        </figure>
      ) : null}
      <div className="flex flex-col gap-3">
        {eyebrow ? (
          <div className="font-sans text-xs font-medium text-ink-eyebrow">{eyebrow}</div>
        ) : null}
        <h1 className="m-0 max-w-[22ch] font-title text-[36px] font-medium leading-[1.12] tracking-[-0.02em] text-ink-primary">
          {headline}
        </h1>
        <p className="m-0 max-w-[60ch] font-sans text-[17px] leading-[1.55] text-ink-body [&_b]:font-medium [&_b]:text-ink-primary [&_em]:italic [&_em]:text-teal-800">
          {body}
        </p>
        {signature ? (
          <div className="mt-2 flex flex-col gap-0.5">
            <span className="font-sans text-[26px] font-light italic leading-none tracking-tight text-teal-800">
              {signature}
            </span>
            {signatureCaption ? (
              <span className="font-sans text-xs font-medium text-ink-secondary">
                {signatureCaption}
              </span>
            ) : null}
          </div>
        ) : null}
        {cta ? <div className="mt-2 flex flex-col items-start gap-2">{cta}</div> : null}
      </div>
    </article>
  );
}
